import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, withRouter, useLocation } from 'react-router-dom';
import { colors } from '@guryou/colors';
import { UserTypes } from '@guryou/core';
import { useEffectOnce } from '@guryou/hooks';
import { Button, FlexColumn, FlexRow, Form, LargeText, PaperBlock, Spinner } from '@guryou/html-components';
import { cognitoFirstLoginSetPassword, cognitoVerifyUser, stateIsLoaded, stateIsLoading } from '@guryou/redux';
import { cognitoPasswordRequirementsForTranslation } from '@guryou/utils';
import { Box, Paper } from '@material-ui/core';
import { persistor } from 'store';
import PasswordInput from './PasswordInput';
import InviteFlow from 'modules/inviteFlow';

const defaultState = {
  password: '',
  confirmPassword: '',
};

const VerifyPassword = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [ls, setLs] = useState(defaultState);
  const firstLogin = useSelector(state => state.cognito.firstLogin);
  const query = new URLSearchParams(useLocation().search);
  const flowId = query.get('flowId');
  const { email, code } = useParams();

  useEffectOnce(() => {
    cognitoVerifyUser(email, code).then(user => setLs(o => ({ ...o, user })));
  });

  const handleInputsChange = (value, e, input) => {
    e.persist();
    setLs(prevState => ({ ...prevState, [input]: value }));
  };

  const handleSubmit = async data => {
    const { newPassword } = data;
    dispatch(cognitoFirstLoginSetPassword({ newPassword }, ls.user, persistor));
  };

  const passwordStrength = cognitoPasswordRequirementsForTranslation(ls.password);
  const checkIfPasswordMatch = ls.password === ls.confirmPassword;

  const successFields = (
    <>
      {firstLogin?.data?.userRole === UserTypes.owner || firstLogin?.data?.userRole === UserTypes.collaborator ? (
        <FlexColumn alignItems="center">
          <LargeText color={colors.white} fontSize="50px">
            {t('WELCOME.titlePlural')}
          </LargeText>
          <Button
            onClick={() => (window.location = `${process.env.REACT_APP_DEAL_URL}/login`)}
            xs={4}
            text={t('MODALS.login')}
            backgroundColor={colors.white}
            paddingTop={4}
            paddingRight={3}
            paddingLeft={3}
            paddingBottom={4}
            marginTop={3}
            fontSize="1.5rem"
          />
        </FlexColumn>
      ) : flowId && ls.user ? (
        <InviteFlow customerFlowId={flowId} email={email} />
      ) : (
        <Paper component={Box} marginBottom={4} padding={2} textAlign="center">
          <LargeText marginTop="25px" marginBottom="15px" color={colors.mineShaft}>
            {t('WELCOME.titlePlural')}
          </LargeText>
        </Paper>
      )}
    </>
  );

  const verifyForm = (
    <PaperBlock xs={11} md={4} lg={4} margin="0 auto">
      {stateIsLoading(firstLogin) ? (
        <Spinner />
      ) : (
        <Form className="login" onSubmit={handleSubmit} actions={false}>
          <h1 className="text-2xl font-semibold text-gray-800 text-center mb-6 p-6">{t('CONFIRMATIONS.enterYourNewPassword')}</h1>
          <div className="grid grid-cols-1 gap-6 px-8 pb-8">
            <PasswordInput
              onChange={(value, e) => handleInputsChange(value, e, 'password')}
              name="newPassword"
              label={t('GENERAL.password')}
              autoComplete="current-password"
              required
            />
            <PasswordInput
              onChange={(value, e) => handleInputsChange(value, e, 'confirmPassword')}
              name="confirmPassword"
              label={t('PROFILE.confirmPass')}
              autoComplete="current-password"
              required
            />

            <button
              type="submit"
              disabled={!!passwordStrength || !checkIfPasswordMatch}
              className={`bg-[var(--feel-primary)] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg min-w-[200px]`}
            >
              {t('GENERAL.save').toUpperCase()}
            </button>
            {typeof passwordStrength === 'string' && passwordStrength && (
              <p className="text-sm text-red-500 mt-1">{t(`PASSWORD_VALIDATIONS.${passwordStrength}`)}</p>
            )}

            {ls.password.length >= 8 && ls.confirmPassword && !checkIfPasswordMatch && (
              <p className="text-sm text-red-500 mt-1">{t('ACCOUNT.newAndConfirmPassDontMatch')}</p>
            )}

            {firstLogin.error && <p className="text-sm text-red-500 mt-1">{firstLogin.error.message}</p>}
          </div>
        </Form>
      )}
    </PaperBlock>
  );

  return (
    <FlexRow xs={12} alignItems="flex-start" minHeight="100%" paddingTop="4vh" paddingBottom="4vh" backgroundColor={colors.mineShaft} margin={0}>
      {stateIsLoaded(firstLogin) ? successFields : verifyForm}
    </FlexRow>
  );
};

export default withRouter(VerifyPassword);
